import { z } from "zod";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { DateTime } from "luxon";
import {
  adminPostRouteReqDtoSchema,
  useAdminRouteV2ControllerCreateVirtualRoute,
  useDriverControllerGetAll,
} from "service";
import {
  AdminGetRoutesResDto,
  AdminPostRouteReqDto,
} from "service/api/estabrookSchemas";
import { ControlledSelectOption } from "ui";

export interface UseVirtualRouteFormArgs {
  date: string; // yyyy-MM-dd
  routePriority: number;
  /**
   * Defaults to "now" (HH:mm) when omitted. Callers that don't surface a
   * start-time field to the user (e.g. the temp-route-from-skipped-orders
   * flow) can just leave this out.
   */
  startTime?: string;
  onSuccess: (route: AdminGetRoutesResDto | undefined) => void;
  /**
   * Optional - called when createVirtualRoute fails, so callers tracking
   * their own "submitting" state can reset it. The global MutationCache
   * already surfaces the error toast.
   */
  onError?: () => void;
}

/**
 * Shared Formik config + driver options + createVirtualRoute mutation for
 * the "virtual route" backend concept. Extracted from CreateVirtualRouteDialog
 * so it and the temp-route-from-skipped-orders flow share identical submit
 * logic. Presentation is left entirely to the caller.
 */
const useVirtualRouteForm = ({
  date,
  routePriority,
  startTime,
  onSuccess,
  onError,
}: UseVirtualRouteFormArgs) => {
  const { data: driversData } = useDriverControllerGetAll({
    queryParams: { limit: 1000, isAvailable: true, hasVehicle: true },
  });

  const driverOptions: ControlledSelectOption[] =
    driversData?.data?.reduce(
      (acc, driver) => {
        if (driver.id && driver.name) {
          acc.push({
            value: driver.id,
            label: driver.name,
            selectedLabel: driver.name,
          });
        }
        return acc;
      },
      [
        {
          label: "Any",
          value: "Any",
        },
        {
          label: "No Driver",
          value: "No Driver",
        },
      ] as ControlledSelectOption[],
    ) || [];

  const { mutate: createVirtualRoute, isLoading: isCreatingRoute } =
    useAdminRouteV2ControllerCreateVirtualRoute({
      onSuccess: (response) => onSuccess(response.data),
      onError: () => onError?.(),
    });

  const formik = useFormik<AdminPostRouteReqDto>({
    initialValues: {
      date,
      routeName: "",
      color: "",
      driverId: null,
      availableForAnyDriver: false,
      routePriority,
      startTime: startTime ?? DateTime.now().toFormat("HH:mm"),
    },
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(
      adminPostRouteReqDtoSchema.extend({
        driverId: z.string(),
      }),
    ),
    onSubmit: (values) => {
      const driverId = values.driverId ?? "";
      const modifiedValues = {
        ...values,
        driverId: ["Any", "No Driver"].includes(driverId) ? null : driverId,
        availableForAnyDriver: driverId === "Any",
        timezone: DateTime.local().zoneName,
      };
      createVirtualRoute({ body: { ...modifiedValues } });
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return { ...formik, driverOptions, isCreatingRoute };
};

export default useVirtualRouteForm;
